import {useInvoices} from "../../../hooks/useInvoices";
import Loader from "../../components/shared/Loader";
import {Fragment, useEffect, useState} from "react";
import ModalViewFacture from "./modalViewFacture";
import {useSelector} from "react-redux";
import {userRoleList} from "../../../utils/constants";
import TablePagination from "./TablePagination";
import {simpleDate} from "../../../utils/date";
import QueryErrorState from "../../components/shared/QueryErrorState";
import {invoiceApi} from "../../../services/invoiceService";
import {showToastModal} from "../../components/shared/ToastModalSwal";
import EmptyState from "../../components/shared/EmptyState";

const TableFacture = () => {
  const [page, setPage] = useState(0);
  const {
    data: factures,
    isLoading,
    isError,
    error,
    refetch,
  } = useInvoices({page: page, size: 10});
  const {selectedRole} = useSelector((state) => state.auth.auth);

  const [open, setOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loadingInvoiceId, setLoadingInvoiceId] = useState(null);
  const [invoiceError, setInvoiceError] = useState("");
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadingInvoiceId, setUploadingInvoiceId] = useState(null);

  useEffect(() => () => {
    if (selectedFile?.url) URL.revokeObjectURL(selectedFile.url);
  }, [selectedFile]);

  if (isLoading) {
    return <Loader className={"relative"} />;
  }

  if (isError) {
    return <QueryErrorState error={error} onRetry={refetch} />;
  }

  const getInvoiceBlobUrl = async (facture) => {
    setLoadingInvoiceId(facture.id);
    setInvoiceError("");
    try {
      const blob = await invoiceApi.getFile(facture.id);
      return URL.createObjectURL(blob);
    } catch (error) {
      setInvoiceError(
        error?.response?.data?.message || "Impossible de charger cette facture.",
      );
      return null;
    } finally {
      setLoadingInvoiceId(null);
    }
  };

  const getProviderBlobUrl = async (facture) => {
    setLoadingInvoiceId(facture.id);
    setInvoiceError("");
    try {
      const blob = await invoiceApi.getProviderFile(facture.id);
      return URL.createObjectURL(blob);
    } catch (error) {
      setInvoiceError(error?.response?.data?.error || "Impossible de charger la facture finale.");
      return null;
    } finally {
      setLoadingInvoiceId(null);
    }
  };

  const uploadProviderInvoice = async (facture, file) => {
    if (!file) return;
    if (file.type !== "application/pdf" || !file.name.toLowerCase().endsWith(".pdf")) {
      setInvoiceError("La facture doit être un fichier PDF.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setInvoiceError("La facture dépasse la taille maximale de 10 Mo.");
      return;
    }
    setUploadingInvoiceId(facture.id);
    setUploadProgress((current) => ({...current, [facture.id]: 0}));
    setInvoiceError("");
    try {
      const result = await invoiceApi.uploadProviderInvoice(
        facture.id,
        file,
        ({loaded, total}) => setUploadProgress((current) => ({
          ...current,
          [facture.id]: total ? Math.round((loaded / total) * 100) : 0,
        })),
      );
      if (result.valid) {
        setInvoiceError("");
        showToastModal({
          success: true,
          title: "Facture validée",
          message: "Votre facture est conforme. Son paiement est programmé pour le lendemain.",
        });
      } else {
        setInvoiceError((result.validation?.errors || [result.message]).join(" "));
      }
      await refetch();
    } catch (error) {
      setInvoiceError(error?.response?.data?.error || "Impossible d’analyser cette facture.");
    } finally {
      setUploadingInvoiceId(null);
    }
  };

  const viewFacture = async (facture) => {
    const url = await getInvoiceBlobUrl(facture);
    if (!url) return;
    setSelectedFile({url, ref: facture?.ref});
    setOpen(true);
  };

  const downloadFacture = async (facture) => {
    const url = await getInvoiceBlobUrl(facture);
    if (!url) return;
    const link = document.createElement("a");
    link.href = url;
    link.download = `${facture?.ref || "facture"}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const toggleInvoiceModal = (value) => {
    setOpen(value);
    if (!value) setSelectedFile(null);
  };

  return (
    <>
      <ModalViewFacture
        openModal={open}
        toggleModal={toggleInvoiceModal}
        fileUrl={selectedFile?.url}
        refrence={selectedFile?.ref}
      />
      {invoiceError && (
        <p className="mb-3 text-sm text-red-600" role="alert">
          {invoiceError}
        </p>
      )}
      <div className="bg-white !px-[14px] !py-4 !rounded-xl !min-h-[70vh]  border border-[#E2E2E2] flex flex-col overflow-x-scroll overflow-style-none">
        {factures?.rows?.length ? (
          <div className="flex flex-col justify-between items-end w-full flex-1 gap-3">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-white text-[#9B9B9B] !font-normal text-xs ">
                  <th className="px-2 py-2 2xl:px-4  text-left !font-normal">
                    Num
                  </th>
                  <th className="px-2 py-2 2xl:px-4  text-left !font-normal  max-[620px]:hidden">
                    Intervention
                  </th>
                  {/* <th className="px-2 py-2 2xl:px-4  text-left !font-normal hidden md:block ">
              Secteur
            </th> */}
                  <th className="px-2 py-2 2xl:px-4  text-left !font-normal  max-[620px]:hidden">
                    {selectedRole === userRoleList.TECHNICIAN
                      ? "Client"
                      : "Prestataire"}
                  </th>
                  <th className="px-2 py-2 2xl:px-4  text-left  !font-normal  max-[620px]:hidden">
                    Date
                  </th>
                  <th className="px-2 py-2 2xl:px-4  text-left !font-normal">
                    Prix
                  </th>
                  <th className="px-2 py-2 2xl:px-4  text-center !font-normal">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {factures?.rows?.map((row, index) => (
                  <Fragment key={row.id || index}>
                  <tr
                    className="even:bg-white bg-[#F1F2F7] text-[#19295C] text-xs font-medium transition-colors"
                  >
                    <td className="px-2 2xl:px-4 py-3">{row?.ref ?? "--"}</td>
                    <td className="px-2 2xl:px-4 py-3 truncate max-w-[200px] max-[620px]:hidden ">
                      {!!row?.interventions?.length
                        ? row?.interventions[0]?.title ?? "--"
                        : "--"}
                    </td>
                    {/* <td className="px-2 2xl:px-4 py-3 hidden md:block ">
                {row.secteur}
              </td> */}
                    <td className="px-2 2xl:px-4 py-3 max-[620px]:hidden capitalize ">
                      {!!row?.interventions?.length
                        ? selectedRole === userRoleList.CLIENT
                          ? row?.interventions[0]?.Offer_Proposal?.Technician
                              ?.User?.lastName +
                            " " +
                            row?.interventions[0]?.Offer_Proposal?.Technician
                              ?.User?.firstName
                          : !!row?.interventions[0]?.Offer_Proposal?.Offer
                          ? row?.interventions[0]?.Offer_Proposal?.Offer?.Client
                              ?.User?.lastName +
                            " " +
                            row?.interventions[0]?.Offer_Proposal?.Offer?.Client
                              ?.User?.firstName
                          : row?.interventions[0]?.Offer_Proposal?.directChat
                              ?.chatroom?.Client?.User?.lastName +
                            " " +
                            row?.interventions[0]?.Offer_Proposal?.directChat
                              ?.chatroom?.Client?.User?.firstName
                        : "--"}
                    </td>
                    <td className="px-2 2xl:px-4 py-3 max-[620px]:hidden">
                      {!!row?.paymentDate ? simpleDate(row?.paymentDate) : "--"}
                    </td>
                    <td className="px-2 2xl:px-4 py-3">
                      {row?.amount ?? "--"}
                    </td>
                    <td className="px-2 2xl:px-4 py-3 text-center flex items-center justify-center gap-2">
                      <span
                        className="text-[#1A64B9] text-[15px] cursor-pointer"
                        onClick={() => viewFacture(row)}
                        aria-disabled={loadingInvoiceId === row.id}
                      >
                        <i className={`fi ${loadingInvoiceId === row.id ? "fi-rr-spinner" : "fi-rr-overview"} flex items-center`}></i>
                      </span>
                      <button
                        type="button"
                        onClick={() => downloadFacture(row)}
                        disabled={loadingInvoiceId === row.id}
                        className="text-[#FF6600] text-[15px] cursor-pointer"
                      >
                        <i className="fi fi-rr-file-download flex items-center "></i>
                      </button>
                    </td>
                  </tr>
                  {selectedRole === userRoleList.TECHNICIAN && row.documentKind === "PRO_FORMA" && (
                    <tr key={`workflow-${row.id}`} className="border-b border-[#E2E2E2] bg-[#FFF8F2]">
                      <td colSpan="6" className="px-4 py-4">
                        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                          <div>
                            <p className="font-semibold text-[#19295C]">Facture professionnelle</p>
                            <p className="text-xs text-[#6B7280]">
                              {row.providerInvoiceStatus === "VALIDATED"
                                ? `Facture validée — paiement prévu le ${row.scheduledPayoutDate || "lendemain"}.`
                                : "Téléchargez la facture exemple, créez votre facture définitive signée avec les mêmes montants, puis envoyez-la ici (PDF, 10 Mo max)."}
                            </p>
                            {row.providerInvoiceStatus === "REJECTED" && (
                              <p className="mt-1 text-xs font-medium text-red-600">
                                {(row.providerInvoiceValidation?.errors || []).join(" ") || "La facture doit être corrigée."}
                              </p>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            {row.providerInvoiceTitle && (
                              <button type="button" onClick={async () => {
                                const url = await getProviderBlobUrl(row);
                                if (!url) return;
                                setSelectedFile({url, ref: `${row.ref}-finale`});
                                setOpen(true);
                              }} className="rounded-lg border border-primary px-4 py-2 text-sm font-semibold text-primary">Voir la facture envoyée</button>
                            )}
                            {row.providerInvoiceStatus !== "VALIDATED" && (
                              <label className={`relative cursor-pointer overflow-hidden rounded-lg bg-secondary px-4 py-2 text-sm font-semibold text-white ${uploadingInvoiceId === row.id ? "pointer-events-none opacity-70" : ""}`}>
                                {uploadingInvoiceId === row.id ? `Envoi ${uploadProgress[row.id] || 0} %` : "Envoyer ma facture finale"}
                                <input type="file" accept="application/pdf,.pdf" className="sr-only" disabled={uploadingInvoiceId === row.id} onChange={(event) => uploadProviderInvoice(row, event.target.files?.[0])} />
                                {uploadingInvoiceId === row.id && <span className="absolute bottom-0 left-0 h-1 bg-primary" style={{width: `${uploadProgress[row.id] || 0}%`}} />}
                              </label>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
              </tbody>
            </table>
            <TablePagination
              page={page}
              setPage={setPage}
              count={Math.max(1, Math.ceil(factures?.totalItems / 10))}
            />
          </div>
        ) : (
          <EmptyState className="h-full flex-1 w-full" />
        )}
      </div>
    </>
  );
};

export default TableFacture;
